import React, { useState } from "react";
import {
  Card,
  Button,
  Form,
  Table,
  Modal,
  InputGroup,
  Spinner,
  // Badge,
} from "react-bootstrap";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconCategory,
  // IconPalette,
} from "@tabler/icons-react";
import { useCategory } from "../context/CategoryContext";
import CategoryForm from "./CategoryForm";
import Swal from "sweetalert2";

const CategoryManagement = () => {
  const {
    categories,
    loading,
    refetch,
    handleSearch,
    // createCategory,
    // updateCategory,
    deleteCategory,
    categoryStats,
    Pagination,
  } = useCategory();

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (category) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete "${category.name}" category?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteCategory(category.id);
        Swal.fire("Deleted!", "Category has been deleted.", "success");
      } catch (error) {
        Swal.fire("Error!", "Failed to delete category.", "error");
      }
    }
  };

  const handleCategoryModalClose = () => {
    setShowCategoryModal(false);
    setSelectedCategory(null);
  };

  const handleCategoryFormSuccess = () => {
    setShowCategoryModal(false);
    setSelectedCategory(null);
    refetch();
    Swal.fire("Success!", "Category has been saved.", "success");
  };

  const handleCategoryFormError = (error) => {
    Swal.fire("Error!", error.message || "Failed to save category.", "error");
  };

  return (
    <>
      <div style={{ marginBottom: "32px" }}>
        {/* Category Statistics */}
        <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
          <Card style={{ flex: 1, backgroundColor: "#e3f2fd" }}>
            <Card.Body style={{ padding: "16px" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <IconCategory size={32} style={{ color: "#1976d2" }} />
                <div>
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: "#1976d2",
                    }}
                  >
                    {categoryStats.loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      categoryStats.total || 0
                    )}
                  </div>
                  <div style={{ fontSize: "14px", color: "#424242" }}>
                    Total Categories
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <IconCategory size={24} />
              Category Management
            </h3>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Organize your inventory items with custom categories and colors
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleCreateCategory}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <IconPlus size={16} />
            Add Category
          </Button>
        </div>

        {/* Category Search */}
        <div style={{ marginBottom: "16px" }}>
          <InputGroup style={{ maxWidth: "400px" }}>
            <InputGroup.Text>
              <IconSearch size={16} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search categories..."
              onKeyPress={handleSearch}
            />
          </InputGroup>
        </div>

        {/* Category Table */}
        <Card>
          <Card.Body style={{ padding: 0 }}>
            <Table striped bordered hover style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Inventories Count</th>
                  <th style={{ textAlign: "center", width: "120px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="4"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      <Spinner animation="border" />
                      <div style={{ marginTop: "8px" }}>
                        Loading categories...
                      </div>
                    </td>
                  </tr>
                ) : categories?.length > 0 ? (
                  categories.map((category) => (
                    <tr key={category.id}>
                      <td>
                        <div style={{ fontWeight: "500", fontSize: "16px" }}>
                          {category.name}
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            maxWidth: "300px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {category.inventories_count || (
                            <span
                              style={{ color: "#9ca3af", fontStyle: "italic" }}
                            >
                              -
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditCategory(category)}
                            title="Edit Category"
                          >
                            <IconEdit size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteCategory(category)}
                            title="Delete Category"
                          >
                            <IconTrash size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <IconCategory size={48} className="text-muted mb-3" />
                        <h5 className="text-muted">No categories found</h5>
                        <p className="text-muted">
                          Start by creating your first category to organize
                          inventory items.
                        </p>
                        <Button
                          variant="primary"
                          onClick={handleCreateCategory}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            margin: "0 auto",
                          }}
                        >
                          <IconPlus size={16} />
                          Create First Category
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>

          {/* Category Pagination */}
          <div className="d-flex justify-content-center mt-3">{Pagination}</div>
        </Card>
      </div>

      {/* Category CRUD Modal */}
      <Modal
        show={showCategoryModal}
        onHide={handleCategoryModalClose}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedCategory ? "Edit Category" : "Create New Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CategoryForm
            initialData={selectedCategory}
            onSuccess={handleCategoryFormSuccess}
            onError={handleCategoryFormError}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CategoryManagement;
